import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ChevronLeft, Lock, FileEdit, UserX, Search, X, Crown, Plus } from "lucide-react";
import { toast } from "sonner";
import { useFb } from "../../utils/football";
import { useAuth } from "../../contexts/AuthContext";

const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="flex-1 rounded-lg bg-slate-900/50 p-2.5 text-center">
    <Icon size={15} className={`mx-auto ${color}`} />
    <div className="mt-1 text-lg font-extrabold text-white">{value}</div>
    <div className="text-[10px] text-slate-400">{label}</div>
  </div>
);

const PointsAdjustmentSection = ({ userId, adjustments, days, onApplied }) => {
  const fb = useFb();
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [matchDay, setMatchDay] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const d = parseInt(delta, 10);
    if (Number.isNaN(d) || d === 0) { toast.error("Enter a non-zero delta"); return; }
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await fb.post("/football/admin/points-adjustment", {
        user_id: userId, delta: d, reason: reason.trim(), match_day: matchDay || null,
      });
      toast.success(`Adjustment applied: ${d > 0 ? "+" : ""}${d} pts`);
      setDelta(""); setReason(""); setMatchDay("");
      onApplied?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to apply adjustment");
    }
    setBusy(false);
  };

  return (
    <div className="mt-4 rounded-xl border border-amber-500/30 bg-slate-900/50 p-3" data-testid="admin-points-adjustment">
      <h3 className="mb-2 text-sm font-extrabold text-amber-300">Points Adjustment</h3>

      {(adjustments || []).length > 0 && (
        <div className="mb-3 space-y-1.5" data-testid="admin-adjustments-list">
          {adjustments.map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-lg bg-slate-800/60 px-2.5 py-1.5 text-xs" data-testid={`admin-adjustment-${a.id}`}>
              <div className="min-w-0">
                <span className={`font-extrabold ${a.delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>{a.delta >= 0 ? "+" : ""}{a.delta}</span>
                <span className="ml-2 text-slate-300">{a.reason}</span>
                {a.match_day && <span className="ml-1 text-slate-500">({a.match_day})</span>}
              </div>
              <span className="shrink-0 text-[10px] text-slate-500">{(a.created_at || "").slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="number" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="±pts"
          className="w-20 rounded-lg border border-border bg-slate-900 px-2 py-2 text-sm text-white outline-none focus:border-amber-500"
          data-testid="adjustment-delta-input"
        />
        <select
          value={matchDay} onChange={(e) => setMatchDay(e.target.value)}
          className="rounded-lg border border-border bg-slate-900 px-2 py-2 text-xs text-white outline-none focus:border-amber-500"
          data-testid="adjustment-matchday-select"
        >
          <option value="">Cumulative</option>
          {(days || []).map((d) => <option key={d.match_day} value={d.match_day}>{d.match_day}</option>)}
        </select>
      </div>
      <input
        value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. goodwill credit)"
        className="mt-2 w-full rounded-lg border border-border bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
        data-testid="adjustment-reason-input"
      />
      <button
        onClick={submit} disabled={busy}
        className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg wc-bg-gold py-2 text-sm font-extrabold text-slate-900 disabled:opacity-50"
        data-testid="adjustment-submit-btn"
      >
        <Plus size={15} /> {busy ? "Applying…" : "Apply adjustment"}
      </button>
    </div>
  );
};

const SquadDetailModal = ({ detail, days, onClose, onRefresh }) => (
  <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/75 p-0 backdrop-blur-sm sm:items-center sm:p-4" data-testid="admin-squad-detail">
    <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-amber-500/30 bg-slate-800 p-5 sm:rounded-2xl">
      <div className="mb-3 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-extrabold text-white">{detail.team_name}</h2>
          <p className="text-xs text-slate-400">{detail.email}</p>
        </div>
        <button onClick={onClose} className="rounded-full p-1 text-slate-400 hover:text-white" data-testid="admin-squad-detail-close"><X size={20} /></button>
      </div>
      <div className="mb-3 flex flex-wrap gap-2 text-xs">
        <span className="rounded-full bg-slate-900 px-2.5 py-1 font-bold wc-gold">{detail.formation}</span>
        <span className={`rounded-full px-2.5 py-1 font-bold ${detail.is_locked ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{detail.is_locked ? "Locked" : "Draft"}</span>
        <span className="rounded-full bg-slate-900 px-2.5 py-1 font-bold text-slate-300">{detail.total_points} pts</span>
      </div>
      <div className="space-y-1.5">
        {detail.squad.map((p) => (
          <div key={p.player_id} className="flex items-center justify-between rounded-lg bg-slate-900/50 px-3 py-2" data-testid={`admin-xi-${p.player_id}`}>
            <div className="flex items-center gap-2">
              <span className="w-9 rounded bg-slate-700 px-1.5 py-0.5 text-center text-[10px] font-bold text-slate-300">{p.position}</span>
              <span className="text-sm font-semibold text-white">{p.name}</span>
              {p.team && <span className="text-[10px] text-slate-500">{p.team}</span>}
            </div>
            <div className="flex items-center gap-1.5">
              {p.is_captain && <span className="flex items-center gap-0.5 rounded bg-amber-400/20 px-1.5 py-0.5 text-[10px] font-bold wc-gold"><Crown size={10} /> C</span>}
              {p.is_vice_captain && <span className="rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-bold text-slate-300">VC</span>}
              {p.is_super_sub && <span className="rounded bg-purple-500/20 px-1.5 py-0.5 text-[10px] font-bold text-purple-300">SUB</span>}
            </div>
          </div>
        ))}
      </div>

      <PointsAdjustmentSection userId={detail.user_id} adjustments={detail.adjustments} days={days} onApplied={onRefresh} />
    </div>
  </div>
);

export default function AdminFootballSquadsPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const { dbUser } = useAuth();
  const [days, setDays] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedDay, setSelectedDay] = useState("");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!dbUser) return;
    (async () => {
      try {
        const d = await fb.get("/football/admin/squads/days");
        setDays(d.days || []);
        setTotalUsers(d.total_users || 0);
        const next = (d.days || []).find((x) => !x.kickoff_passed) || (d.days || [])[0];
        if (next) setSelectedDay(next.match_day);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbUser]);

  useEffect(() => {
    if (!selectedDay) return;
    setLoading(true);
    (async () => {
      try { const d = await fb.get(`/football/admin/squads/day/${selectedDay}`, q ? { q } : {}); setRows(d.rows || []); }
      catch { setRows([]); }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, q]);

  const openUser = async (userId) => {
    try { setDetail(await fb.get(`/football/admin/squads/user/${userId}/${selectedDay}`)); } catch { /* ignore */ }
  };

  const refreshDetail = async () => {
    if (detail?.user_id) await openUser(detail.user_id);
  };

  const dayMeta = days.find((d) => d.match_day === selectedDay);

  if (dbUser && !dbUser.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="admin-football-squads-page">
      <div className="wc-hero-gradient px-4 pb-5 pt-4">
        <button onClick={() => navigate("/admin")} className="mb-2 inline-flex items-center gap-1 text-sm text-white/80" data-testid="admin-squads-back"><ChevronLeft size={18} /> Admin</button>
        <h1 className="text-xl font-extrabold text-white">Football — Squads</h1>
        <p className="mt-1 text-sm text-white/70">Read-only squad visibility for launch operations.</p>
      </div>

      <div className="mx-auto max-w-md px-4">
        {/* Day picker */}
        <div className="mt-4 flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="admin-squads-day-picker">
          {days.map((d) => (
            <button
              key={d.match_day}
              onClick={() => setSelectedDay(d.match_day)}
              className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs font-bold transition-all ${selectedDay === d.match_day ? "wc-bg-gold border-amber-400 text-slate-900" : "border-border bg-card text-muted-foreground"}`}
              data-testid={`admin-squads-day-${d.match_day}`}
            >
              {d.match_day} <span className="opacity-70">({d.fixtures}⚽)</span>
            </button>
          ))}
        </div>

        {/* Participation summary */}
        {dayMeta && (
          <div className="mt-3 flex gap-2" data-testid="admin-squads-summary">
            <Stat icon={Lock} label="Locked" value={dayMeta.locked} color="text-emerald-400" />
            <Stat icon={FileEdit} label="Draft" value={dayMeta.draft} color="text-amber-400" />
            <Stat icon={UserX} label="Not started" value={dayMeta.not_started} color="text-slate-400" />
          </div>
        )}
        <p className="mt-1 text-center text-[11px] text-slate-500">{totalUsers} total users</p>

        {/* Search */}
        <div className="relative mt-3">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or team name"
            className="w-full rounded-lg border border-border bg-slate-900 py-2 pl-9 pr-3 text-sm text-white outline-none focus:border-amber-500"
            data-testid="admin-squads-search"
          />
        </div>

        {/* User list */}
        <div className="mt-3 space-y-2">
          {loading ? <div className="h-24 animate-pulse rounded-xl bg-secondary/50" /> :
            rows.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground" data-testid="admin-squads-empty">No squads for this day yet.</p> :
              rows.map((r) => (
                <button
                  key={r.user_id}
                  onClick={() => openUser(r.user_id)}
                  className="flex w-full items-center justify-between rounded-xl border border-border bg-card px-3 py-2.5 text-left"
                  data-testid={`admin-squad-row-${r.user_id}`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-white">{r.team_name}</p>
                    <p className="truncate text-xs text-slate-500">{r.email}</p>
                  </div>
                  <div className="ml-2 flex shrink-0 items-center gap-2">
                    <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold wc-gold">{r.formation}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.is_locked ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{r.is_locked ? "Locked" : "Draft"}</span>
                  </div>
                </button>
              ))}
        </div>
      </div>

      {detail && <SquadDetailModal detail={detail} days={days} onClose={() => setDetail(null)} onRefresh={refreshDetail} />}
    </div>
  );
}
